// Centralized bottleneck resolver for the Dashboard.
//
// The Dashboard is the command surface — its bottleneck callout
// needs to reflect the whole pipeline's worst state, not just the
// earliest empty stage. The pre-C4 version walked lifecycle stages
// only, so an active publish-failure or kill-switch-engaged got
// shadowed by an irrelevant "no captures yet" message that
// triggered as soon as a freshly-archived idea left the captures
// list empty.
//
// Per-tab bottleneck strings stay where they are — they describe
// each surface's local concerns. Only the Dashboard delegates here.
//
// Priority order (highest → lowest):
//   1. Kill switch engaged              — every gated publish blocks
//   2. Publish failures with retries    — dead-letter posts in queue
//   3. Open critical / error incidents  — autonomy resolver flagged
//      something the operator must triage
//   4. Required integrations down       — credentials / verification
//      failures gate publish + sync
//   5. Truth / proof empty + drafts in  — drafts can be written but
//      motion                           not defended
//   6. Lifecycle-empty stages           — earliest empty stage in
//                                       the feeder pipeline
//
// Each level returns null when the level doesn't apply, so the
// caller stacks them with || in priority order.

function ifString(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

/**
 * Returns the bottleneck message for level 1 (kill switch). Pure.
 */
export function killSwitchBottleneck(state) {
  if (!state?.killSwitchEngaged) return null
  const reason = ifString(state.killSwitchReason)
  return reason
    ? `Autonomy is paused (${reason}). Resume from Autonomy → State once the cause is resolved.`
    : 'Autonomy is paused. Resume from Autonomy → State to reopen the publish path.'
}

/**
 * Returns the bottleneck message for level 2 (publish failures).
 */
export function publishFailureBottleneck({ publishFailedCount = 0 } = {}) {
  if (publishFailedCount <= 0) return null
  const noun = publishFailedCount === 1 ? 'attempt' : 'attempts'
  return `${publishFailedCount} publish ${noun} failed. Open Posts → Queue and recover before queueing more.`
}

/**
 * Returns the bottleneck message for level 3 (open incidents).
 * Only counts severity error / critical so a pillar-deprecated info
 * incident doesn't outrank pipeline work.
 */
export function incidentBottleneck({ incidents = [] } = {}) {
  if (!Array.isArray(incidents)) return null
  const open = incidents.filter(
    (incident) =>
      !incident.resolvedAt &&
      (incident.severity === 'error' || incident.severity === 'critical')
  )
  if (open.length === 0) return null
  const noun = open.length === 1 ? 'incident' : 'incidents'
  return `${open.length} open ${noun}. Resolve or downgrade them before promoting any content type to auto.`
}

/**
 * Returns the bottleneck message for level 4 (integration health).
 * `integrationHealth.integrations` is a map of `{ enabled, available }`
 * — an enabled-but-not-available integration is the operator's gap
 * to close.
 */
export function integrationHealthBottleneck({ integrationHealth } = {}) {
  const entries = Object.values(integrationHealth?.integrations || {})
  const broken = entries.filter(
    (entry) => entry?.enabled === true && entry?.available === false
  )
  if (broken.length === 0) return null
  const noun = broken.length === 1 ? 'integration is' : 'integrations are'
  return `${broken.length} required ${noun} unavailable. Open Settings → Integrations to reconnect.`
}

/**
 * Returns the bottleneck message for level 5 (truth/proof empty
 * while drafts in motion). Drafts can be written without a truth
 * record, but they can't be defended — the operator should know.
 */
export function defenselessBottleneck({
  truthCount = 0,
  proofCount = 0,
  draftsInMotion = 0,
} = {}) {
  if (truthCount > 0 || proofCount > 0) return null
  if (draftsInMotion <= 0) return null
  return 'Drafts in motion but no truth positions or proof assets. Public claims cannot be defended.'
}

/**
 * Returns the bottleneck message for level 6 (lifecycle-empty
 * stages). Earliest empty stage wins — the lifecycle is a feeder
 * pipeline, so the earliest gap gates everything downstream.
 */
export function lifecycleEmptyBottleneck({
  ideas = 0,
  truth = 0,
  proof = 0,
  drafts = 0,
  queue = 0,
  acceptedTopicsWithoutProof = 0,
} = {}) {
  if (ideas === 0)
    return 'No captures yet. Capture is the entry point — a claim, signal, or technical observation.'
  if (truth === 0 && proof === 0)
    return 'No truth positions or proof assets. Drafts can be written, but they cannot be defended.'
  if (proof === 0 && acceptedTopicsWithoutProof > 0)
    return 'Accepted topics have no proof attached. Public claims need defensible evidence.'
  if (drafts === 0 && queue === 0 && ideas > 0)
    return 'Captures exist but no drafts in motion. Open Compose to convert one.'
  return null
}

/**
 * Resolve the highest-priority bottleneck for the Dashboard's
 * command-surface callout. Returns null when nothing's blocking.
 *
 * The state object is the union of every signal the Dashboard
 * already has access to — pass it through verbatim from
 * `buildDashboardState` and the autonomy snapshot.
 */
export function resolveDashboardBottleneck(state = {}) {
  return (
    killSwitchBottleneck(state.autonomyState) ||
    publishFailureBottleneck(state) ||
    incidentBottleneck(state) ||
    integrationHealthBottleneck(state) ||
    defenselessBottleneck(state) ||
    lifecycleEmptyBottleneck(state) ||
    null
  )
}
